import { Autocomplete, TextField, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { ProductFiltersFormValues } from "../filters/ProductFilterFields.js";
import { useProductFilterFields_AvailableFiltersQuery } from "../filters/ProductFilterFields.generated.js";
import { useProductSearchModalQuery } from "../modals/ProductSearchModal.generated.js";
import { useApolloClient } from "@apollo/client";
import { getDataOrNull, assertNever } from "@msys/common";
import { highlightWithBold } from "../../../utils.js";
import { unionBy } from "lodash-es";
import debounce from "lodash.debounce";
import { ProductSearchItem__ProductSearchResultFragment } from "../Product.generated.js";
import { useMemo, useState, useEffect } from "react";
import { useTranslate } from "@tolgee/react";

const BRAND_SUGGESTION_THRESHOLD = 0;
const PRODUCT_SUGGESTION_THRESHOLD = 2;
const MAX_SUGGESTIONS = 5;
const DEBOUNCE_DELAY = 300;

type Suggestion =
  | {
      type: "brand" | "brandLine";
      text: string;
      data: { key: string; count: number };
    }
  | {
      type: "product";
      text: string;
      data: ProductSearchItem__ProductSearchResultFragment;
    };

function topMatches(
  items: { label: string; key: string; count: number }[],
  limit: number,
  searchTerm: string
): { label: string; key: string; count: number }[] {
  const results: { label: string; key: string; count: number }[] = [];
  results.push(
    ...items
      .filter(i => i.label.toLowerCase().startsWith(searchTerm))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
      )
      .slice(0, limit)
  );

  if (results.length < limit) {
    const suffixes = items
      .filter(
        i =>
          i.label.toLowerCase().endsWith(searchTerm) &&
          !results.some(r => r.label === i.label)
      )
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
      )
      .slice(0, limit - results.length);
    results.push(...suffixes);
  }

  return results;
}

export function ProductSearchAutocomplete({
  searchTerm,
  placeholder,
  onChangeSearchTerm,
  onPick,
  onSubmit,
  sx,
  filters,
}: {
  searchTerm: string;
  placeholder: string;
  onChangeSearchTerm: (value: string) => void;
  onPick: (value: Suggestion) => void;
  onSubmit: () => void;
  filters: ProductFiltersFormValues;
  sx?: SxProps<Theme>;
}) {
  const client = useApolloClient();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((term: string) => setDebouncedSearchTerm(term), DEBOUNCE_DELAY),
    []
  );

  useEffect(() => {
    debouncedSetSearchTerm(searchTerm);
    return () => debouncedSetSearchTerm.cancel();
  }, [searchTerm, debouncedSetSearchTerm]);

  const suggestBrands = debouncedSearchTerm.length > BRAND_SUGGESTION_THRESHOLD;
  const suggestBrandLines =
    debouncedSearchTerm.length > BRAND_SUGGESTION_THRESHOLD;
  const suggestProducts =
    debouncedSearchTerm.length > PRODUCT_SUGGESTION_THRESHOLD;
  const normalizedSearchTerm = debouncedSearchTerm
    .toLowerCase()
    .replace(/\s+/g, "");

  const availableFiltersQuery = useProductFilterFields_AvailableFiltersQuery({
    client,
    variables: {
      filters,
    },
    skip: !suggestBrands && !suggestBrandLines,
  });

  const availableFilters = getDataOrNull(
    availableFiltersQuery.data?.pimSearchProductsAvailableFilters
  );

  const brands =
    availableFilters?.brands?.map(b => ({
      label: b.label,
      key: b.key,
      count: b.count,
    })) ?? [];
  const brandLines =
    availableFilters?.brandLines?.map(b => ({
      label: b.label,
      key: b.key,
      count: b.count,
    })) ?? [];

  const brandSuggestions = suggestBrands
    ? topMatches(brands, MAX_SUGGESTIONS, normalizedSearchTerm).map(b => ({
        type: "brand" as const,
        text: b.label,
        data: { key: b.key, count: b.count },
      }))
    : [];
  const brandLineSuggestions = suggestBrandLines
    ? topMatches(brandLines, MAX_SUGGESTIONS, normalizedSearchTerm).map(bl => ({
        type: "brandLine" as const,
        text: bl.label,
        data: { key: bl.key, count: bl.count },
      }))
    : [];

  const articleNumberQuery = useProductSearchModalQuery({
    client,
    variables: {
      searchTerm: undefined,
      specificFieldQueryFields: [
        {
          field: "articleNumber",
          value: debouncedSearchTerm,
        },
      ],
      filters,
      limit: 5,
      offset: 0,
    },
    skip: !suggestProducts,
  });
  const titleQuery = useProductSearchModalQuery({
    client,
    variables: {
      searchTerm: undefined,
      specificFieldQueryFields: [
        {
          field: "titleText",
          value: debouncedSearchTerm,
        },
      ],
      filters,
      limit: MAX_SUGGESTIONS,
      offset: 0,
    },
    skip: !suggestProducts,
  });

  const articleNumberProducts =
    getDataOrNull(articleNumberQuery.data?.pimSearchProducts)?.edges.map(
      e => e.node
    ) ?? [];

  const titleProducts =
    getDataOrNull(titleQuery.data?.pimSearchProducts)?.edges.map(e => e.node) ??
    [];

  const products = unionBy(articleNumberProducts, titleProducts, p => p.id);

  const productSuggestions = suggestProducts
    ? products.slice(0, 5).map(p => ({
        type: "product" as const,
        text: p.texts?.title
          ? `${p.texts.title} ${p.articleNumber}`
          : `${p.articleNumber}`,
        data: p,
      }))
    : [];

  const suggestions: Suggestion[] = [
    ...productSuggestions,
    ...brandSuggestions,
    ...brandLineSuggestions,
  ];

  const { t } = useTranslate(["Product", "Global"]);

  const translations: Record<"brand" | "brandLine" | "product", string> = {
    product: t("Products", { ns: "Product" }),
    brand: t("Brands", { ns: "Product" }),
    brandLine: t("Brand lines", { ns: "Product" }),
  };

  return (
    <Autocomplete
      sx={{
        ...sx,
        "& .MuiFilledInput-root": {
          minHeight: 32,
          height: 32,
          paddingTop: 0,
          paddingBottom: 0,
        },
        "& .MuiFilledInput-input": { paddingTop: 0.5, paddingBottom: 0.5 },
      }}
      filterOptions={x => x}
      noOptionsText={t("No options", { ns: "Global" })}
      options={suggestions}
      getOptionLabel={opt => (typeof opt === "string" ? opt : opt.text)}
      renderOption={(props, option) => (
        <li {...props}>
          <span style={{ whiteSpace: "pre-wrap" }}>
            {highlightWithBold(
              option.text,
              option.type === "product" ? searchTerm : normalizedSearchTerm
            )}
          </span>
        </li>
      )}
      groupBy={option => translations[option.type]}
      inputValue={searchTerm}
      onInputChange={(_, newValue, reason) => {
        if (reason === "input") onChangeSearchTerm(newValue);
      }}
      onChange={(_, option) => {
        if (option && typeof option !== "string") {
          onPick(option);
        } else if (typeof option === "string") {
          onChangeSearchTerm(option);
          onSubmit();
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={placeholder && `${placeholder}…`}
          size="small"
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSubmit();
            }
          }}
          slotProps={{
            ...params.slotProps,

            htmlInput: {
              ...params.slotProps.htmlInput,
              autoComplete: "off",
            },
          }}
        />
      )}
    />
  );
}
